import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, IconButton, Alert, CircularProgress,
  Tabs, Tab, TextField, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, TablePagination, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import MessageIcon from '@mui/icons-material/Message';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { useAdminFetch } from '../hooks/useAdminFetch';

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  created_at: string;
  is_read: boolean;
  replied_at: string | null;
  reply_text?: string | null;
  replied_by?: string | null;
}

interface EmailSignup {
  id: number;
  name: string;
  email: string;
  origin: string;
  created_at: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminMessages: React.FC = () => {
  const navigate = useNavigate();
  const adminFetch = useAdminFetch();
  const apiUrl = process.env.REACT_APP_API_URL;

  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Messages state
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [messagesSearch, setMessagesSearch] = useState('');
  const [messagesPage, setMessagesPage] = useState(0);
  const [messagesPerPage, setMessagesPerPage] = useState(20);
  const [messagesTotalCount, setMessagesTotalCount] = useState(0);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);

  // Email signups state
  const [signups, setSignups] = useState<EmailSignup[]>([]);
  const [signupsLoading, setSignupsLoading] = useState(true);
  const [signupsError, setSignupsError] = useState<string | null>(null);
  const [signupsSearch, setSignupsSearch] = useState('');
  const [signupsPage, setSignupsPage] = useState(0);
  const [signupsPerPage, setSignupsPerPage] = useState(20);
  const [signupsTotalCount, setSignupsTotalCount] = useState(0);

  // View message dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessage | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Reply dialog state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'message' | 'signup'; id: number; name: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Success message
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load messages
  const loadMessages = useCallback(async () => {
    setMessagesLoading(true);
    setMessagesError(null);

    try {
      const params = new URLSearchParams({
        page: String(messagesPage + 1),
        per_page: String(messagesPerPage),
      });
      if (messagesSearch) {
        params.append('search', messagesSearch);
      }

      const data = await adminFetch(`${apiUrl}/api/admin/messages/?${params}`);
      setMessages(data.messages);
      setMessagesTotalCount(data.total);
      setMessagesUnreadCount(data.unread_count);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setMessagesError(error.message || 'Failed to load messages');
      }
    } finally {
      setMessagesLoading(false);
    }
  }, [apiUrl, messagesPage, messagesPerPage, messagesSearch, adminFetch]);

  // Load email signups
  const loadSignups = useCallback(async () => {
    setSignupsLoading(true);
    setSignupsError(null);

    try {
      const params = new URLSearchParams({
        page: String(signupsPage + 1),
        per_page: String(signupsPerPage),
      });
      if (signupsSearch) {
        params.append('search', signupsSearch);
      }

      const data = await adminFetch(`${apiUrl}/api/admin/email-signups/?${params}`);
      setSignups(data.signups);
      setSignupsTotalCount(data.total);
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setSignupsError(error.message || 'Failed to load signups');
      }
    } finally {
      setSignupsLoading(false);
    }
  }, [apiUrl, signupsPage, signupsPerPage, signupsSearch, adminFetch]);

  useEffect(() => {
    if (tabValue === 0) {
      loadMessages();
    } else {
      loadSignups();
    }
  }, [tabValue, loadMessages, loadSignups]);

  // Handle view message
  const handleViewMessage = async (messageId: number) => {
    setViewLoading(true);
    setViewDialogOpen(true);

    try {
      const data = await adminFetch(`${apiUrl}/api/admin/messages/${messageId}/`);
      setSelectedMessage(data);
      // Update the message in the list to show as read
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, is_read: true } : m));
      setMessagesUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setMessagesError(error.message || 'Failed to load message');
      }
      setViewDialogOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  // Handle reply
  const handleOpenReply = () => {
    setReplyText('');
    setReplyError(null);
    setReplyDialogOpen(true);
  };

  const handleSendReply = async () => {
    if (!selectedMessage || !replyText.trim()) {
      setReplyError('Please enter a reply message');
      return;
    }

    setReplyLoading(true);
    setReplyError(null);

    try {
      await adminFetch(`${apiUrl}/api/admin/messages/${selectedMessage.id}/reply/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reply: replyText }),
      });
      // Close both dialogs
      setReplyDialogOpen(false);
      setViewDialogOpen(false);
      setSelectedMessage(null);
      setSuccessMessage('Reply sent successfully!');
      setTimeout(() => setSuccessMessage(null), 4000);
      // Reload messages to update replied status
      loadMessages();
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setReplyError(error.message || 'Failed to send reply');
      }
    } finally {
      setReplyLoading(false);
    }
  };

  // Handle delete
  const handleOpenDelete = (type: 'message' | 'signup', id: number, name: string) => {
    setDeleteTarget({ type, id, name });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);

    try {
      const endpoint = deleteTarget.type === 'message'
        ? `${apiUrl}/api/admin/messages/${deleteTarget.id}/`
        : `${apiUrl}/api/admin/email-signups/${deleteTarget.id}/`;

      await adminFetch(endpoint, { method: 'DELETE' });

      setDeleteDialogOpen(false);
      setSuccessMessage(deleteTarget.type === 'message' ? 'Message deleted' : 'Signup removed');
      setTimeout(() => setSuccessMessage(null), 4000);

      // Close view dialog if deleting the currently viewed message
      if (deleteTarget.type === 'message' && selectedMessage?.id === deleteTarget.id) {
        setViewDialogOpen(false);
        setSelectedMessage(null);
      }

      // Reload the list
      if (deleteTarget.type === 'message') {
        loadMessages();
      } else {
        loadSignups();
      }
    } catch (err: unknown) {
      const error = err as Error;
      if (!error.message?.includes('Admin authentication')) {
        setMessagesError(error.message || 'Failed to delete');
      }
    } finally {
      setDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Box className="page-header">
        <Box className="title-row" sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/admin')} aria-label="back to admin">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1" className="page-title">View Messages</Typography>
        </Box>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab
            icon={<MessageIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Contact Form Messages
                {messagesUnreadCount > 0 && (
                  <Chip label={messagesUnreadCount} size="small" color="primary" />
                )}
              </Box>
            }
          />
          <Tab
            icon={<MailOutlineIcon />}
            iconPosition="start"
            label={`Mailing List Signups (${signupsTotalCount})`}
          />
        </Tabs>
      </Box>

      {/* Contact Form Messages Tab */}
      <TabPanel value={tabValue} index={0}>
        {messagesError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMessagesError(null)}>
            {messagesError}
          </Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search messages by name, email, subject, or content..."
          value={messagesSearch}
          onChange={(e) => { setMessagesSearch(e.target.value); setMessagesPage(0); }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={40}></TableCell>
                <TableCell>From</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Preview</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messagesLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : messages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No messages found
                  </TableCell>
                </TableRow>
              ) : (
                messages.map((msg) => (
                  <TableRow
                    key={msg.id}
                    hover
                    onClick={() => handleViewMessage(msg.id)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: msg.is_read ? 'inherit' : 'action.hover',
                      fontWeight: msg.is_read ? 'normal' : 'bold',
                    }}
                  >
                    <TableCell>
                      {msg.is_read ? (
                        <MarkEmailReadIcon color="disabled" fontSize="small" />
                      ) : (
                        <MarkEmailUnreadIcon color="primary" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: msg.is_read ? 'normal' : 'bold' }}>
                      {msg.name}<br />
                      <Typography variant="caption" color="text.secondary">{msg.email}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontWeight: msg.is_read ? 'normal' : 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {msg.subject}
                        {msg.replied_at && (
                          <Chip label="Replied" size="small" color="success" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                        {msg.message}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(msg.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDelete('message', msg.id, msg.subject)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={messagesTotalCount}
            rowsPerPage={messagesPerPage}
            page={messagesPage}
            onPageChange={(_, newPage) => setMessagesPage(newPage)}
            onRowsPerPageChange={(e) => { setMessagesPerPage(parseInt(e.target.value, 10)); setMessagesPage(0); }}
          />
        </TableContainer>
      </TabPanel>

      {/* Mailing List Signups Tab */}
      <TabPanel value={tabValue} index={1}>
        {signupsError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSignupsError(null)}>
            {signupsError}
          </Alert>
        )}

        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search signups by name, email, or origin..."
          value={signupsSearch}
          onChange={(e) => { setSignupsSearch(e.target.value); setSignupsPage(0); }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Origin</TableCell>
                <TableCell>Signed Up</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signupsLoading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : signups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No signups found
                  </TableCell>
                </TableRow>
              ) : (
                signups.map((signup) => (
                  <TableRow key={signup.id}>
                    <TableCell>{signup.name}</TableCell>
                    <TableCell>{signup.email}</TableCell>
                    <TableCell>{signup.origin}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {formatDate(signup.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove from list">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDelete('signup', signup.id, signup.email)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={signupsTotalCount}
            rowsPerPage={signupsPerPage}
            page={signupsPage}
            onPageChange={(_, newPage) => setSignupsPage(newPage)}
            onRowsPerPageChange={(e) => { setSignupsPerPage(parseInt(e.target.value, 10)); setSignupsPage(0); }}
          />
        </TableContainer>
      </TabPanel>

      {/* View Message Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {selectedMessage?.subject || 'Loading...'}
          </Box>
          {selectedMessage && (
            <Chip
              label={formatDate(selectedMessage.created_at)}
              size="small"
              variant="outlined"
            />
          )}
        </DialogTitle>
        <DialogContent>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedMessage && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">From:</Typography>
                <Typography variant="body1">
                  {selectedMessage.name} &lt;{selectedMessage.email}&gt;
                </Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">Message:</Typography>
                <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: 'grey.50', whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.message}
                </Paper>
              </Box>
              {selectedMessage.replied_at && (
                <Box sx={{ mb: 2, mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip label="Replied" size="small" color="success" />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(selectedMessage.replied_at)}
                    </Typography>
                  </Box>
                  <Typography variant="subtitle2" color="text.secondary">Your Reply:</Typography>
                  <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: 'success.50', whiteSpace: 'pre-wrap' }}>
                    {selectedMessage.reply_text}
                  </Paper>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="error"
            onClick={() => selectedMessage && handleOpenDelete('message', selectedMessage.id, selectedMessage.subject)}
          >
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<ReplyIcon />}
            onClick={handleOpenReply}
            disabled={!selectedMessage}
          >
            Reply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Dialog */}
      <Dialog open={replyDialogOpen} onClose={() => setReplyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Reply to {selectedMessage?.name}</DialogTitle>
        <DialogContent>
          {replyError && (
            <Alert severity="error" sx={{ mb: 2 }}>{replyError}</Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">To:</Typography>
            <Typography variant="body1">{selectedMessage?.email}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">Subject:</Typography>
            <Typography variant="body1">Re: {selectedMessage?.subject}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Original Message:</Typography>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.100', maxHeight: 150, overflow: 'auto' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                {selectedMessage?.message}
              </Typography>
            </Paper>
          </Box>
          <TextField
            label="Your Reply"
            multiline
            rows={6}
            fullWidth
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Type your reply here..."
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReplyDialogOpen(false)} disabled={replyLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            onClick={handleSendReply}
            disabled={replyLoading || !replyText.trim()}
            startIcon={replyLoading ? <CircularProgress size={20} /> : <ReplyIcon />}
          >
            {replyLoading ? 'Sending...' : 'Send Reply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>
          {deleteTarget?.type === 'message' ? 'Delete Message' : 'Remove Signup'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteTarget?.type === 'message'
              ? `Are you sure you want to delete this message: "${deleteTarget?.name}"?`
              : `Are you sure you want to remove ${deleteTarget?.name} from the mailing list?`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminMessages;
